import { Component, Input, AfterViewInit, OnDestroy, ElementRef, ViewChild, NgZone, OnChanges, SimpleChanges } from '@angular/core';

interface VirtualItem {
  index: number;
  data: any;
}

@Component({
  selector: 'nc-virtual-scroll',
  templateUrl: './virtual-scroll.component.html',
  styleUrls: ['./virtual-scroll.component.less']
})
export class VirtualScrollComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('viewport', { static: true }) viewport!: ElementRef;//视窗
  @ViewChild('templateContainer') templateContainer!: ElementRef;//用于获取传入的item高度，设置itemHeight值

  /** 列表数据 */
  @Input() ncItems: any[] = [];
  /** 列表项模板 */
  @Input() ncItemTemplate: any;//传入的item模板
  /** 缓冲item数量，前后各2项 */
  @Input() ncBufferCount: number = 2;

  initialized = false;   // 标记 itemHeight 是否已初始化（模板中 *ngIf 使用，需为 public）
  private viewInitialized = false; // 标记 ngAfterViewInit 是否已执行（templateContainer 才可用）
  private resizeObserver!: ResizeObserver; // 监听 viewport 容器尺寸变化

  visibleItems: VirtualItem[] = [];//显示items
  totalPadding: number = 0;//总填充高度
  topPadding: number = 0;
  scrollTop: number = 0;//已滚动高度
  itemHeight: number = 1;//若设为0，下方代码获取不到this.templateContainer
  viewportHeight: number = 0;//视窗可视高度

  constructor(private ngZone: NgZone) { }


  /**
   * 场景	触发行	原因
   * 初始就有值（同步）	第 49 行measureAndUpdate	ngOnChanges 先执行但 viewInitialized=false 跳过，由 ngAfterViewInit 补调
   * 初始无值，后续赋值（异步）	第 75 行measureAndUpdate	ngAfterViewInit 时无数据跳过，数据到达时 ngOnChanges 直接调用
   */

  ngAfterViewInit() {
    this.viewportHeight = this.viewport.nativeElement.clientHeight;
    this.viewInitialized = true;
    // 若 ncItems 在 ngAfterViewInit 之前就已同步赋值，则在此补充执行初始化
    if (!this.initialized && this.ncItems.length > 0) {
      this.measureAndUpdate();
    }
    // 监听 viewport 容器高度变化，在 Angular 区域外观察以避免频繁触发变更检测
    this.ngZone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(entries => {
        const newHeight = entries[0].contentRect.height;
        if (newHeight !== this.viewportHeight) {
          this.ngZone.run(() => {
            this.viewportHeight = newHeight;
            this.updateVisibleItems();
          });
        }
      });
      this.resizeObserver.observe(this.viewport.nativeElement);
    });
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['ncItems'] && this.ncItems.length > 0) {
      if (!this.initialized) {
        // templateContainer 仅在 ngAfterViewInit 后可用，未初始化则等待
        if (this.viewInitialized) {
          this.measureAndUpdate();
        }
        // 否则由 ngAfterViewInit 补调，无需处理
      } else {
        this.updateTotalPadding();
        this.updateVisibleItems();
      }
    }
  }

  /** 测量 itemHeight 并更新列表（需在 ngAfterViewInit 之后调用） */
  private measureAndUpdate() {
    // setTimeout(0) 确保 Angular 已完成本次 DOM 渲染
    setTimeout(() => {
      const element = this.templateContainer?.nativeElement?.firstChild;
      if (element) {
        const styles = getComputedStyle(element);
        this.itemHeight = element.offsetHeight + parseFloat(styles.marginTop) + parseFloat(styles.marginBottom);
        this.initialized = true;
      }
      this.updateTotalPadding();
      this.updateVisibleItems();
    });
  }

  onScroll() {
    this.ngZone.run(() => {
      this.scrollTop = this.viewport.nativeElement.scrollTop;
      this.updateVisibleItems();
    });
  }

  updateVisibleItems() {
    const firstVisibleIndex = Math.floor(this.scrollTop / this.itemHeight);
    const startIndex = Math.max(0, firstVisibleIndex - this.ncBufferCount);
    const visibleItemsCount = Math.ceil(this.viewportHeight / this.itemHeight);
    const endIndex = Math.min(firstVisibleIndex + visibleItemsCount + this.ncBufferCount, this.ncItems.length - 1);

    this.visibleItems = [];
    for (let i = startIndex; i <= endIndex; i++) {
      this.visibleItems.push({ index: i, data: this.ncItems[i] });
    }

    this.topPadding = startIndex * this.itemHeight;
  }

  updateTotalPadding() {
    this.totalPadding = this.ncItems.length * this.itemHeight;
  }

  //采用trackBy提升性能，只渲染改变的dom
  trackByIndex(index: number, item: VirtualItem): number {
    return item.index;
  }
}
